import { Loader2 } from "lucide-react";
import {
  Children,
  forwardRef,
  isValidElement,
  createContext,
  useContext,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

import { colorValue } from "./utils";

import {
  Avatar as UiAvatar,
  AvatarImage as UiAvatarImage,
  AvatarFallback as UiAvatarFallback,
} from "@/components/ui/avatar";
import { Button as UiButton } from "@/components/ui/button";
import { Checkbox as UiCheckbox } from "@/components/ui/checkbox";
import {
  RadioGroup as UiRadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import {
  Select as UiSelect,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch as UiSwitch } from "@/components/ui/switch";
import { Textarea as UiTextarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export type { ButtonProps } from "@/components/ui/button";

const SIZE_MAP: Record<string, string> = {
  "1": "xs",
  "2": "sm",
  "3": "default",
  "4": "lg",
};
const ICON_SIZE_MAP: Record<string, string> = {
  "1": "icon-xs",
  "2": "icon-sm",
  "3": "icon",
  "4": "icon-lg",
};

/** Radix Themes <Button> -> shadcn Button (test-management styling). */
export const Button = forwardRef<HTMLButtonElement, any>(function Button(
  {
    loading,
    variant,
    size,
    color: _color,
    highContrast: _hc,
    radius: _r,
    ...rest
  },
  ref,
) {
  const v = variant === "classic" ? "default" : variant;
  const s =
    typeof size === "string" && size in SIZE_MAP ? SIZE_MAP[size] : size;
  return (
    <UiButton
      ref={ref as any}
      variant={v}
      size={s as any}
      isLoading={loading}
      {...rest}
    />
  );
});

/** Radix Themes <IconButton> -> square shadcn Button. */
export const IconButton = forwardRef<HTMLButtonElement, any>(
  function IconButton(
    {
      loading,
      variant,
      size,
      color,
      highContrast: _hc,
      radius: _r,
      className,
      ...rest
    },
    ref,
  ) {
    const s =
      typeof size === "string" && size in ICON_SIZE_MAP
        ? ICON_SIZE_MAP[size]
        : "icon";
    const colorCls =
      color === "tomato" || color === "red"
        ? "text-destructive hover:bg-destructive/10 border-destructive/30"
        : "";
    return (
      <UiButton
        ref={ref as any}
        variant={variant ?? "ghost"}
        size={s as any}
        isLoading={loading}
        className={cn(colorCls, className)}
        {...rest}
      />
    );
  },
);

/** Radix Themes <Badge> -> tinted pill (test-management badge styling, with color support). */
export const Badge = forwardRef<HTMLSpanElement, any>(function Badge(
  {
    color,
    variant = "soft",
    size: _size,
    radius,
    highContrast: _hc,
    className,
    style,
    children,
    ...rest
  },
  ref,
) {
  const c = colorValue(color) ?? "hsl(var(--muted-foreground))";
  const tint: CSSProperties =
    variant === "solid"
      ? { backgroundColor: c, color: "white" }
      : variant === "outline" || variant === "surface"
        ? {
            border: `1px solid color-mix(in oklab, ${c} 35%, transparent)`,
            color: c,
            backgroundColor: "transparent",
          }
        : {
            backgroundColor: `color-mix(in oklab, ${c} 16%, transparent)`,
            color: c,
          };
  return (
    <span
      ref={ref}
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap px-2 py-0.5 text-xs font-medium leading-tight",
        radius === "full" ? "rounded-full" : "rounded-md",
        className,
      )}
      style={{ ...tint, ...(style as CSSProperties) }}
      {...rest}
    >
      {children}
    </span>
  );
});

/** Radix Themes <Checkbox> -> shadcn Checkbox. */
export const Checkbox = forwardRef<HTMLButtonElement, any>(function Checkbox(
  {
    size: _size,
    color: _color,
    variant: _variant,
    highContrast: _hc,
    radius: _r,
    ...rest
  },
  ref,
) {
  return <UiCheckbox ref={ref as any} {...rest} />;
});

/** Radix Themes <Switch> -> shadcn Switch. */
export const Switch = forwardRef<HTMLButtonElement, any>(function Switch(
  {
    size: _size,
    color: _color,
    variant: _variant,
    highContrast: _hc,
    radius: _r,
    ...rest
  },
  ref,
) {
  return <UiSwitch ref={ref as any} {...rest} />;
});

/** Radix Themes <TextArea> -> shadcn Textarea. */
export const TextArea = forwardRef<HTMLTextAreaElement, any>(function TextArea(
  {
    size: _size,
    variant: _variant,
    color: _color,
    radius: _r,
    resize,
    style,
    ...rest
  },
  ref,
) {
  return (
    <UiTextarea
      ref={ref as any}
      style={{ resize, ...(style as CSSProperties) }}
      {...rest}
    />
  );
});

/* ─────────────── TextField (compound: Root + Slot) ─────────────── */

function TextFieldRoot({
  className,
  children,
  size: _size,
  variant: _variant,
  color: _color,
  radius: _r,
  style,
  ...inputProps
}: any) {
  const left: ReactNode[] = [];
  const right: ReactNode[] = [];
  Children.forEach(children, (child) => {
    if (isValidElement(child)) {
      const side = (child.props as any)?.side ?? "left";
      (side === "right" ? right : left).push(child);
    }
  });
  return (
    <div
      className={cn(
        "flex h-9 w-full items-center gap-1.5 rounded-md border border-input bg-background px-3 text-sm shadow-sm transition-colors focus-within:ring-1 focus-within:ring-ring",
        className,
      )}
      style={style as CSSProperties}
    >
      {left}
      <input
        className="h-full min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
        {...inputProps}
      />
      {right}
    </div>
  );
}

function TextFieldSlot({ className, children, side: _side, ...rest }: any) {
  return (
    <span
      className={cn(
        "flex shrink-0 items-center text-muted-foreground",
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}

export const TextField = Object.assign(TextFieldRoot, {
  Root: TextFieldRoot,
  Slot: TextFieldSlot,
});

/* ─────────────── Spinner ─────────────── */

const SPINNER_PX: Record<string, number> = {
  xs: 14,
  sm: 16,
  md: 18,
  lg: 22,
  xl: 28,
  "1": 16,
  "2": 18,
  "3": 20,
};

export function Spinner({
  size,
  loading = true,
  children,
  className,
  label,
  inline: _inline,
  ...rest
}: any) {
  if (loading === false) return <>{children ?? null}</>;
  const px = SPINNER_PX[String(size)] ?? 16;
  return (
    <Loader2
      className={cn("animate-spin text-current", className)}
      style={{ width: px, height: px }}
      aria-label={label ?? "Loading"}
      {...rest}
    />
  );
}

/* ─────────────── Avatar ─────────────── */

const AVATAR_PX: Record<string, number> = {
  "1": 24,
  "2": 32,
  "3": 40,
  "4": 48,
  "5": 64,
  "6": 80,
  "7": 96,
  "8": 128,
  "9": 160,
};

export const Avatar = forwardRef<HTMLSpanElement, any>(function Avatar(
  {
    src,
    fallback,
    size,
    radius,
    color: _color,
    highContrast: _hc,
    className,
    style,
    ...rest
  },
  ref,
) {
  const px = AVATAR_PX[String(size)] ?? 32;
  return (
    <UiAvatar
      ref={ref as any}
      className={cn(
        radius === "full" ? "rounded-full" : "rounded-md",
        className,
      )}
      style={{ width: px, height: px, ...(style as CSSProperties) }}
      {...rest}
    >
      {src ? <UiAvatarImage src={src} alt={(rest as any).alt ?? ""} /> : null}
      <UiAvatarFallback>{fallback}</UiAvatarFallback>
    </UiAvatar>
  );
});

/* ─────────────── Radio / RadioGroup ─────────────── */

function RadioGroupRoot({ children, ...rest }: any) {
  return <UiRadioGroup {...rest}>{children}</UiRadioGroup>;
}
function RadioGroupItemCompat({
  color: _color,
  size: _size,
  variant: _v,
  highContrast: _hc,
  ...rest
}: any) {
  return <RadioGroupItem {...rest} />;
}
export const RadioGroup = Object.assign(RadioGroupRoot, {
  Root: RadioGroupRoot,
  Item: RadioGroupItemCompat,
});

export function Radio({
  className,
  color: _color,
  size: _size,
  variant: _v,
  ...rest
}: any) {
  return (
    <input
      type="radio"
      className={cn(
        "h-4 w-4 shrink-0 cursor-pointer accent-primary border-input",
        className,
      )}
      {...rest}
    />
  );
}

/* ─────────────── SegmentedControl ─────────────── */

const SegCtx = createContext<{ value?: string; setValue: (v: string) => void }>(
  {
    setValue: () => {},
  },
);

function SegmentedRoot({
  value: controlled,
  defaultValue,
  onValueChange,
  className,
  children,
  size: _size,
  radius,
  ...rest
}: any) {
  const [uncontrolled, setUncontrolled] = useState<string | undefined>(
    defaultValue,
  );
  const value = controlled ?? uncontrolled;
  const setValue = (v: string) => {
    if (controlled == null) setUncontrolled(v);
    onValueChange?.(v);
  };
  return (
    <SegCtx.Provider value={{ value, setValue }}>
      <div
        className={cn(
          "inline-flex items-center gap-0.5 bg-muted p-0.5 text-muted-foreground",
          radius === "full" ? "rounded-full" : "rounded-lg",
          className,
        )}
        {...rest}
      >
        {children}
      </div>
    </SegCtx.Provider>
  );
}

function SegmentedItem({ value, className, children, onClick, ...rest }: any) {
  const ctx = useContext(SegCtx);
  const active = ctx.value === value;
  return (
    <button
      type="button"
      data-state={active ? "active" : "inactive"}
      onClick={(e) => {
        ctx.setValue(value);
        onClick?.(e);
      }}
      className={cn(
        "inline-flex items-center justify-center rounded-md px-2.5 py-1 text-sm font-medium transition-colors cursor-pointer",
        active
          ? "bg-background text-foreground shadow-sm"
          : "hover:text-foreground",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export const SegmentedControl = Object.assign(SegmentedRoot, {
  Root: SegmentedRoot,
  Item: SegmentedItem,
});

/* ─────────────── Select (compound) ─────────────── */

function SelectRoot({ children, ...rest }: any) {
  return <UiSelect {...rest}>{children}</UiSelect>;
}
function SelectTriggerCompat({
  placeholder,
  children,
  className,
  color: _c,
  variant: _v,
  radius: _r,
  ...rest
}: any) {
  return (
    <SelectTrigger className={className} {...rest}>
      {children ?? <SelectValue placeholder={placeholder} />}
    </SelectTrigger>
  );
}
function SelectContentCompat({
  variant: _v,
  color: _c,
  size: _s,
  highContrast: _hc,
  ...rest
}: any) {
  return <SelectContent {...rest} />;
}
export const Select = Object.assign(SelectRoot, {
  Root: SelectRoot,
  Trigger: SelectTriggerCompat,
  Content: SelectContentCompat,
  Item: SelectItem,
  Group: SelectGroup,
  Label: SelectLabel,
  Separator: SelectSeparator,
});

/* ─────────────── Callout (compound) ─────────────── */

function CalloutRoot({
  color,
  variant: _v,
  className,
  children,
  style,
  ...rest
}: any) {
  const c = colorValue(color) ?? "hsl(var(--info))";
  return (
    <div
      className={cn(
        "flex items-start gap-2 rounded-md border p-3 text-sm",
        className,
      )}
      style={{
        borderColor: `color-mix(in oklab, ${c} 30%, transparent)`,
        backgroundColor: `color-mix(in oklab, ${c} 8%, transparent)`,
        color: c,
        ...(style as CSSProperties),
      }}
      {...rest}
    >
      {children}
    </div>
  );
}
function CalloutIcon({ className, children, ...rest }: any) {
  return (
    <span className={cn("mt-0.5 inline-flex shrink-0", className)} {...rest}>
      {children}
    </span>
  );
}
function CalloutText({
  className,
  children,
  size: _s,
  color: _c,
  ...rest
}: any) {
  return (
    <span className={cn("text-foreground/90", className)} {...rest}>
      {children}
    </span>
  );
}
export const Callout = Object.assign(CalloutRoot, {
  Root: CalloutRoot,
  Icon: CalloutIcon,
  Text: CalloutText,
});
